import React from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { FileExtensionResult } from 'stores/WorkSpaceStoreAdapter';

type Props = {
  anchorEl: HTMLElement | null;
  extensions: FileExtensionResult[];
  onClose: () => void;
  /** 拡張子を選択したときに呼ばれる（extension は 'java' などの小文字） */
  onSelect: (extension: string) => void;
  /** サブメニューとして表示する場合のアンカー位置調整 */
  anchorOrigin?: React.ComponentProps<typeof Menu>['anchorOrigin'];
  transformOrigin?: React.ComponentProps<typeof Menu>['transformOrigin'];
  /**
   * ホバーで表示するサブメニューとして使う場合 true。
   * 親メニューを閉じずに操作できるよう非モーダル化し、バックドアでホバーを妨げない。
   */
  submenu?: boolean;
  /** サブメニュー上に入った/出たときのハンドラ（ホバー維持用） */
  onMenuEnter?: () => void;
  onMenuLeave?: () => void;
};

/**
 * 作成する拡張子を選択するメニュー（拡張子マスタから生成）。
 * 上部の新規ファイル作成、コンテキストメニューの「ファイルを作成」から利用する。
 */
const ExtensionMenu: React.FC<Props> = ({
  anchorEl,
  extensions,
  onClose,
  onSelect,
  anchorOrigin,
  transformOrigin,
  submenu,
  onMenuEnter,
  onMenuLeave,
}) => {
  return (
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin ?? { vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={transformOrigin ?? { vertical: 'top', horizontal: 'left' }}
      // サブメニュー時: 非モーダル化してホバー操作を可能にする
      hideBackdrop={submenu}
      disableAutoFocus={submenu}
      disableEnforceFocus={submenu}
      disableRestoreFocus={submenu}
      // バックドロップ領域でホバーを奪わないようにし、Paper 側のみ操作可能にする
      sx={submenu ? { pointerEvents: 'none' } : undefined}
      slotProps={{
        paper: {
          elevation: 0,
          onMouseEnter: onMenuEnter,
          onMouseLeave: onMenuLeave,
          sx: {
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            minWidth: 160,
            ...(submenu ? { pointerEvents: 'auto' } : {}),
          },
        },
      }}
    >
      {extensions.map((ext) => (
        <MenuItem
          key={ext.extension}
          onClick={() => {
            onSelect(ext.extension);
            onClose();
          }}
        >
          <ListItemIcon>
            <DescriptionOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{ext.label}</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            .{ext.extension}
          </Typography>
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ExtensionMenu;
